import L from 'leaflet'
import 'leaflet-play/dist/LeafletPlayback.min.js'
import store from '@/store'
import _ from 'lodash'
import {
    SET_SELECTED_CLASSIFICATION_ID,
    SET_PREVIOUS_SELECTED_CLASSIFICATION_ID,
} from '@/store/mutations.type'
import { READ_GEOJSON_REPLAY } from '@/store/actions.type'

async function validateAndFixGeoJSON(data) {
    const features = Array.isArray(data.features) ? data.features : data;

    const fixedFeatures = features.filter((feature) => {
        const coords = feature.geometry.coordinates;
        const times = feature.properties.time;
        const device = feature.properties.device;

        // Pomijamy niepoprawne geometrie lub dane czasowe
        if (
            feature.geometry.type !== "MultiPoint" ||
            !Array.isArray(coords) ||
            !Array.isArray(times) ||
            coords.length !== times.length
        ) {
            console.warn(`Urządzenie=${device} błąd danych — pomijam.`);
            return false;
        }

        // Usuwamy punkty z duplikatami kolejnych timestampów (powodują dzielenie przez 0 w LeafletPlayback)
        const dedupedCoords = [coords[0]];
        const dedupedTimes = [times[0]];
        for (let i = 1; i < times.length; i++) {
            if (times[i] !== times[i - 1]) {
                dedupedCoords.push(coords[i]);
                dedupedTimes.push(times[i]);
            }
        }
        feature.geometry.coordinates = dedupedCoords;
        feature.properties.time = dedupedTimes;

        // Jeśli po deduplikacji zostało mniej niż 2 punkty — pomijamy ten device
        if (dedupedTimes.length < 2) {
            console.warn(`Urządzenie=${device} błąd danych[czasu] — pomijam.`);
            return false;
        }
        return true;
    });

    return {
        type: "FeatureCollection",
        features: fixedFeatures,
    };
}


export async function initPlayback(map, fromTime, toTime) {
    let options = {
        tickLen: 30,
        speed: 0.1,
        tracksLayer: false,
        playControl: false,
        dateControl: false,
        sliderControl: false,
        popups: false,
        marker: () => {
            return {
                icon: L.divIcon({
                    // className: 'my-div-icon',
                    iconUrl: 'https://dostartu.pl/gfx/oval-small.svg',
                    iconAnchor: [4, 4],
                    popupAnchor: [0, 0],
                }),
            }
        },
    }

    const fromTimestamp = Date.parse(fromTime)
    const toTimestamp = Date.parse(toTime)
    const devicesSummary = store.getters.devicesSummary
    const devicesIds = []
    const includedDeviceIds = new Set()

    for (const player of Object.values(store.getters.players)) {
        if (!player.deviceId || includedDeviceIds.has(player.deviceId)) {
            continue
        }

        const summary = devicesSummary[player.deviceId]
        if (!summary || !summary.startTime || !summary.endTime) {
            continue
        }

        const summaryStart = Date.parse(summary.startTime)
        const summaryEnd = Date.parse(summary.endTime)
        if (
            !Number.isFinite(fromTimestamp) ||
            !Number.isFinite(toTimestamp) ||
            !Number.isFinite(summaryStart) ||
            !Number.isFinite(summaryEnd) ||
            summaryStart > toTimestamp ||
            summaryEnd < fromTimestamp
        ) {
            continue
        }

        includedDeviceIds.add(player.deviceId)
        devicesIds.push(player.deviceId)
    }

    const geoJSON = devicesIds.length
        ? await store.dispatch(READ_GEOJSON_REPLAY, {
            from: fromTime,
            to: toTime,
            deviceId: devicesIds,
        })
        : []

    const validatedGeoJSON = await validateAndFixGeoJSON(geoJSON);

    const playback = new L.Playback(map, validatedGeoJSON, null, options)
    const markers = {}
    let competition = store.getters.competitions[map.options.competitionId]
    const polylines = {} // Store polylines for each device

    function bringToFront(deviceId) {
        if (polylines[deviceId]) {
            polylines[deviceId].bringToFront();
        }
    }

    playback._map.eachLayer(function (layer) {
        if (layer.feature?.properties.device) {
            let device = store.getters.devices[layer.feature.properties.device]
            if (!device) {
                console.error(`Brak device w store dla device id=${layer.feature.properties.device} — pomijam.`)
                return;
            }
            let player = _.find(store.getters.players, player => {
                return player.deviceId === device.id
            })

            // Initialize a polyline for the device
            const polyline = L.polyline([], {
                color: player.deviceColor || '#FF3C01', // Use the device color or a default color
                weight: 3,     // Line thickness
            }).addTo(map)
            polylines[device.id] = polyline
            // markers[device.id] = layer;

            polyline.on('mouseover', () => bringToFront(device.id));
            // layer.on('mouseover', () => bringToFront(device.id));

            polyline.on('click', () => bringToFront(device.id));
            // layer.on('click', () => bringToFront(device.id));

            layer
                .bindTooltip(
                    L.tooltip({
                        opacity: 1,
                        offset: [-5, -30],
                        className: 'custom-tooltip',
                        direction: 'center',
                    })
                )
                .setTooltipContent(
                    `${player.id
                        ? `${player.firstname} ${player.lastname}`
                        : player.specialName
                    }`
                )

            layer.on('mouseover', event => {
                event.target.openTooltip()
                if (
                    player &&
                    player.classification &&
                    player.classification !==
                    store.getters.selectedClassificationId
                ) {
                    store.commit(
                        SET_PREVIOUS_SELECTED_CLASSIFICATION_ID,
                        store.getters.selectedClassificationId
                    )
                    store.commit(
                        SET_SELECTED_CLASSIFICATION_ID,
                        player.classification
                    )
                }
            })

            // Update the polyline on marker movement
            layer.on('move', (event) => {
                let startTime = playback.getStartTime()
                let endTime = playback.getEndTime()
                if (store.getters.selectedSerieNumber) {
                    startTime = store.getters.serieTimesInMs.serieStartTime / 1000
                    endTime = store.getters.serieTimesInMs.serieEndTime / 1000
                }
                const currentTime = playback.getTime();
                const times = store.getters.playbackMarkers[device.id].feature.properties.time;
                const coords = store.getters.playbackMarkers[device.id].feature.geometry.coordinates;

                const devicePath = [];
                for (let i = 0; i < times.length; i++) {
                    const timestamp = times[i];

                    if (timestamp >= startTime && timestamp <= currentTime && timestamp <= endTime) {
                        const [lng, lat] = coords[i];

                        const last = devicePath[devicePath.length - 1];
                        if (!last || last[0] !== lat || last[1] !== lng) {
                            devicePath.push([lat, lng]);
                        }
                    }
                }

                polylines[device.id].setLatLngs(devicePath);
                polylines[device.id].addLatLng(event.target.getLatLng());
            })

            // Change classification on marker click.
            layer.on('click', () => {
                if (
                    player &&
                    competition._classifications().length >= 2 &&
                    !player.specialName
                ) {
                    store.commit(
                        SET_SELECTED_CLASSIFICATION_ID,
                        player.classification
                    )
                    store.commit(SET_PREVIOUS_SELECTED_CLASSIFICATION_ID, null)
                }
            })
            layer.on('mouseout', event => {
                if (
                    device.id !== store.getters.focusDeviceId &&
                    !store.getters.isShowAllNames
                ) {
                    event.target.closeTooltip()
                }
                if (store.getters.previousSelectedClassificationId) {
                    store.commit(
                        SET_SELECTED_CLASSIFICATION_ID,
                        store.getters.previousSelectedClassificationId
                    )
                    store.commit(SET_PREVIOUS_SELECTED_CLASSIFICATION_ID, null)
                }
            })
            layer.on('tooltipclose', event => {
                if (store.getters.isShowAllNames) {
                    setTimeout(() => event.target.openTooltip(), 200)
                }
            })

            // Toggle polyline visibility based on active classification and player selection
            const updatePolylineVisibility = () => {
                const isLiveMode = store.getters.isLiveMode
                const selectedClassificationId = store.getters.selectedClassificationId
                const awardedDeviceIds = store.getters.awardedDeviceIds

                // Remove polyline if isLiveMode is active
                if (isLiveMode) {
                    // map.removeLayer(polylines[device.id])
                    polylines[device.id].setLatLngs([]) // Remove polyline
                    return
                }

                // Show polyline only if the player matches the selected classification and is awarded
                if (player.classification === selectedClassificationId && awardedDeviceIds.includes(device.id)) {
                    map.addLayer(polylines[device.id]) // Show polyline
                } else {
                    map.removeLayer(polylines[device.id]) // Hide polyline
                }
            }

            // Watch for changes in the active classification
            store.watch(
                () => ({
                    isLiveMode: store.getters.isLiveMode,
                    selectedClassificationId: store.getters.selectedClassificationId,
                    awardedDeviceIds: store.getters.awardedDeviceIds,
                }),
                updatePolylineVisibility,
                { deep: true }
            )
            // Initial visibility check
            updatePolylineVisibility()
            markers[device.id] = layer
        }
    })

    return { playback, markers, polylines }
}

export function isAwardedMarker(marker) {
    if (marker) {
        return store.getters.awardedDeviceIds.includes(
            marker.feature.properties.device
        )
    }
    return false
}

export function isLocatedInTrackBoundsZone(marker) {
    let selectedClassificationId = store.getters.selectedClassificationId
    let tracksBounds = store.getters.tracksBounds[selectedClassificationId]
    return tracksBounds.contains(marker.getLatLng())
}

export function hideMarker(marker) {
    if (marker) {
        marker._icon.classList.add('dnone')
        marker._tooltip.setOpacity(0)
    }
}

export function showMarker(marker) {
    if (marker) {
        marker._icon.classList.remove('dnone')
        marker._tooltip.setOpacity(0.9)
    }
}
export function hasSelectedClassification(player) {
    let selectedClassificationId = store.getters.selectedClassificationId
    return player.classification === selectedClassificationId
}
export function isSpecial(player) {
    return Boolean(player.specialName)
}
export function setHighlightMarker(marker, player) {
    marker.setIcon(
        L.divIcon({
            className: 'my-div-icon',
            // iconUrl: 'https://dostartu.pl/gfx/oval-small.svg',
            iconAnchor: [10, 10],
            popupAnchor: [0, 0],
            html:
                `<div v-if="${player.playerNumber}" class="player-number">
                    ${player.playerNumber}
                </div>
                <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 12 12">
                  <circle r="5" transform="matrix(1 0 0 1 6 6)" fill="#FF3C01" fill-rule="evenodd" stroke="#37474f" stroke-width="1" />
                  <circle r="1.5" transform="matrix(1 0 0 1 6 6)" fill="${player.deviceColor}" fill-rule="evenodd" stroke="#37474f" stroke-width="0.75" />
                </svg>`
        }),
    )
}
export function setNormalMarker(marker, player) {
    marker.setIcon(
        L.divIcon({
            className: 'my-div-icon',
            // iconUrl: 'https://dostartu.pl/gfx/oval-small.svg',
            iconAnchor: [8, 8],
            popupAnchor: [0, 0],
            html:
                `<div v-if="${player.playerNumber}" class="player-number">
                    ${player.playerNumber}
                </div>
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 12 12">
                  <circle r="5" transform="matrix(1 0 0 1 6 6)" fill="#FFFFFF" fill-rule="evenodd" stroke="#37474f" stroke-width="1" />
                  <circle r="2" transform="matrix(1 0 0 1 6 6)" fill="${player.deviceColor}" fill-rule="evenodd" stroke="#37474f" stroke-width="0.75" />
                </svg>`
        }),
    )
}
export function setDisabledMarker(marker, player) {
    marker.setIcon(
        L.divIcon({
            className: 'my-div-icon',
            // iconUrl: 'https://dostartu.pl/gfx/oval-small.svg',
            iconAnchor: [10, 10],
            popupAnchor: [0, 0],
            html:
                `<div v-if="${player.playerNumber}" class="player-number">
                    ${player.playerNumber}
                </div>
                <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 12 12">
                  <circle r="5" transform="matrix(1 0 0 1 6 6)" fill="#a0b1b9" fill-rule="evenodd" stroke="#37474f" stroke-width="1" />
                  <circle r="1.5" transform="matrix(1 0 0 1 6 6)" fill="${player.deviceColor}" fill-rule="evenodd" stroke="#37474f" stroke-width="0.75" />
                </svg>
                `
        }),
    )
}
